/**
 * API service for interacting with the backend server.
 * Firebase auth is imported dynamically to avoid circular dependencies.
 */

// Define the API base URL
   // Define the API base URL based on environment
   const BASE_URL = process.env.REACT_APP_API_URL || 
     (window.location.hostname === 'localhost' 
       ? 'http://localhost:3001/api'
       : 'https://cricketai.onrender.com/api');
/**. 
 * Sends a message to the AI service and returns the response
 * @param {string} message - The user's message
 * @param {boolean} isStockAnalysis - Whether the message is for stock analysis
 * @returns {Promise<{text: string, citations: string[], structuredData: any | null}>} - The AI response
 */
export const getAIResponse = async (message, isStockAnalysis = false) => {
    console.log(`Sending ${isStockAnalysis ? 'stock analysis' : 'chat'} request for: ${message.substring(0, 30)}...`);
    
    // Add request timestamp for freshness verification
    const requestTimestamp = Date.now();
    
    try {
        // Set a longer timeout for the model (90 seconds to account for the larger model)
        const timeoutMs = 90000;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        
        // Prepare the request data
        const requestData = {
            message,
            isStockAnalysis,
            requestTimestamp,
            useSpecificModel: true // Ensure we use the specific model configuration
        };
        
        // Send the request to the API
        const response = await fetch('/api/chat', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(await getAuthHeader())
            },
            body: JSON.stringify(requestData),
            signal: controller.signal
        });
        
        // Clear the timeout
        clearTimeout(timeoutId);
        
        // Check if the request was successful
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error('API error:', errorData);
            
            // Create a more descriptive error message based on the status code
            let errorMessage = errorData.message || errorData.error || 'Failed to get AI response';
            
            // If we have model info, add it to the error
            if (errorData.modelInfo) {
                console.log('Model info from error:', errorData.modelInfo);
                
                // For preview models, provide more specific error messages
                if (errorData.modelInfo.isPreview) {
                    if (errorMessage.includes('permission') || errorMessage.includes('403')) {
                        errorMessage = `The preview model (${errorData.modelInfo.name}) requires specific API key permissions. Please check your API key configuration.`;
                    } else if (errorMessage.includes('not found')) {
                        errorMessage = `The preview model (${errorData.modelInfo.name}) was not found. This model may not be publicly available yet.`;
                    } else if (errorMessage.includes('timeout') || errorMessage.includes('deadline')) {
                        errorMessage = `The preview model response timed out. These models may have higher latency than stable releases.`;
                    }
                }
            }
            
            throw new Error(errorMessage);
        }
        
        // Parse the response
        const data = await response.json();
        console.log('API response received:', data.response ? 'success' : 'no response');
        
        // Log model info if available
        if (data.modelInfo) {
            console.log('Model info:', data.modelInfo);
        }
        
        // Extract the response text
        const responseText = data.response || '';
        
        if (!responseText) {
            console.warn('Empty response received from AI');
            throw new Error('Empty response received from AI');
        }
        
        // Extract citations if available
        let citations = [];
        if (data.groundingData && data.groundingData.citations) {
            citations = data.groundingData.citations.map(citation => {
                // Extract domain from URL for better display
                let domain = '';
                try {
                    const url = new URL(citation.url);
                    domain = url.hostname.replace('www.', '');
                } catch (e) {
                    domain = citation.url;
                }
                
                // Get date if available
                let dateStr = '';
                if (citation.publicationDate) {
                    try {
                        const date = new Date(citation.publicationDate);
                        dateStr = date.toLocaleDateString();
                    } catch (e) {
                        // Ignore date parsing errors
                    }
                }
                
                return {
                    ...citation,
                    domain,
                    displayText: dateStr ? `${citation.text} (${dateStr})` : citation.text
                };
            });
            
            console.log(`Processed ${citations.length} citations`);
        }
        
        // For stock analysis, apply additional processing
        let processedText = responseText;
        let structuredData = data.structuredData || {};
        
        if (isStockAnalysis) {
            // Format numerical values for better readability
            processedText = responseText.replace(
                /(\$[\d,]+\.?\d*|\d+\.?\d*%|[\d,]+\.?\d*)/g,
                match => `**${match}**`
            );
            
            // Format interpretation sections for better visibility
            processedText = processedText.replace(
                /INTERPRETATION:?\s*\((.*?)\)/gi,
                (match, interpretation) => `\n\n**INTERPRETATION**: _${interpretation}_\n`
            );
        }
        
        // Return the processed response
        return {
            text: processedText,
            citations,
            structuredData,
            modelInfo: data.modelInfo || {
                name: "AI model",
                timestamp: new Date().toISOString()
            },
            rawResponse: data
        };
    } catch (error) {
        console.error('Error in getAIResponse:', error);
        
        // Check if the error is due to a timeout
        if (error.name === 'AbortError') {
            throw new Error('Request timed out. The operation took too long to complete. Please try again or simplify your request.');
        }
        
        throw error;
    }
};

export const analyzeStock = async (ticker) => {
    console.log(`Analyzing stock: ${ticker}`);
    
    // Validate input - accept any non-empty string (ticker or company name)
    if (!ticker || !ticker.trim()) {
        throw new Error('Please enter a stock ticker symbol or company name');
    }
    
    try {
        // Display loading message to user
        return await getAIResponse(ticker.trim(), true);
    } catch (error) {
        console.error(`Stock analysis error for ${ticker}:`, error);
        throw error;
    }
};

// Helper function to get auth headers from Firebase
export const getAuthHeader = async () => {
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      console.warn('No authenticated user found');
      return {}; // Return empty object when not authenticated
    }
    
    const token = await user.getIdToken(true);
    return {
      'Authorization': `Bearer ${token}`
    };
  } catch (error) {
    console.error('Failed to get auth header:', error);
    return {}; // Return empty object on error
  }
};